import React, { use, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiService } from 'services/ApiService';
import { Project } from 'models/projects/Project';
import { Team } from 'models/teams/Team';
import { utilities } from 'utils/utilities';

interface FollowedArtist {
  artistUserID: string;
  artistUserName: string;
  location?: string;
}

const normalizeProject = (p: any): Project => {
  const proj = new Project();
  proj.projectId = p.projectId ?? proj.projectId;
  proj.userId = p.userId ?? proj.userId;
  proj.teamId = p.teamId ?? proj.teamId;
  proj.workflowId = p.workflowId ?? proj.workflowId;
  proj.name = p.name ?? proj.name;
  proj.description = p.description ?? proj.description;
  proj.status = typeof p.status === 'number' ? p.status : Number(p.status ?? proj.status);
  proj.ownerName = p.ownerName ?? proj.ownerName;
  proj.enrollmentSubmissions = Array.isArray(p.enrollmentSubmissions) ? p.enrollmentSubmissions : [];
  proj.team = p.team ?? proj.team;
  return proj;
};

const UserProfilePage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [followedArtists, setFollowedArtists] = useState<FollowedArtist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userId = utilities.getUserId();

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch user's projects
        const projectsResponse = await ApiService.getProjectsBySearch({
          artistId: '',
          workflowId: '',
          projectId: '',
          createPartialProject: false,
          teamId: ''
        });

        if (projectsResponse.success && projectsResponse.data) {
          // Filter projects where user is the owner and normalize them
          const userProjects = projectsResponse.data
            .filter(p => p.userId === userId)
            .map(p => normalizeProject(p));
          setProjects(userProjects);
        }

        // Fetch followed artists
        // TODO: Add API endpoint to get followed artists
        // const artistsResponse = await ApiService.getFollowedArtists(userId);
        // if (artistsResponse.success) setFollowedArtists(artistsResponse.data);

      } catch (err) {
        console.error('Failed to fetch user data:', err);
        setError('Failed to load your dashboard. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  useEffect(() => {
    const fetchTeams = async () => {
      setLoading(true);
      setError(null);

      try {
        const teamsResponse = await ApiService.getUserTeams(utilities.getUserId())
        if (teamsResponse.success && teamsResponse.data) setTeams(teamsResponse.data);
      } catch (err) {
        console.error('Failed to fetch teams:', err);
        setError('Failed to load your teams. Please try again.');
      }
    }
    fetchTeams();
  }, [userId]);

  useEffect(() => {
    const fetchArtistsFollowing = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await ApiService.getArtistsFollowing(userId);
        if (response.success && response.data) {
          setFollowedArtists(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch your followed artists:', err);
        setError('Failed to load your followed artists. Please try again.');
      }
    }
    fetchArtistsFollowing();
  }, [userId]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">My Dashboard</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Projects Section */}
        <section className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-gray-900">My Projects</h2>
            <span className="text-sm text-gray-500">{projects.length} total</span>
          </div>

          {projects.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-600 mb-4">You don't have any projects yet.</p>
              <p className="text-sm text-gray-500">
                Projects will appear here when you start working with artists.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {projects.map(project => (
                <Link
                  key={project.projectId}
                  to={`/artist/${project.teamId}/workflows/${project.workflowId}/projects/${project.projectId}`}
                  className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {project.name || project.ownerName}
                      </h3>
                      {project.description && (
                        <p className="text-sm text-gray-600 mb-3">{project.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{project.getStatus()}</span>
                        <span>•</span>
                        <span>{project.enrollmentSubmissions.length} steps</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      (typeof project.status === 'number' ? project.status : Number(project.status ?? -1)) === 0
                        ? 'bg-green-100 text-green-800'
                        : (typeof project.status === 'number' ? project.status : Number(project.status ?? -1)) === 1
                        ? 'bg-red-100 text-red-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {project.getStatus()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Sidebar */}
        <aside className="space-y-8">
          {/* Teams Section */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">My Teams (Member of)</h2>
            {teams.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-sm text-gray-600">No teams yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teams.map(team => (
                  <Link
                  key={team.teamId}
                  to={`/artist/${team.artistId}/operations`}
                  className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div
                    key={team.teamId}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <h3 className="font-medium text-gray-900">{team.teamName}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {team.teammates?.length || 1} members
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

          {/* Followed Artists Section */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Following</h2>
            {followedArtists.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-sm text-gray-600 mb-3">
                  Not following any artists yet.
                </p>
                <Link
                  to="/home"
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Discover artists
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {followedArtists.map(artist => (
                  <Link
                    key={artist.artistUserID}
                    to={`/artist/${artist.artistUserID}`}
                    className="block bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                  >
                    <h3 className="font-medium text-gray-900">{artist.artistUserName}</h3>
                    {artist.location && (
                      <p className="text-sm text-gray-500 mt-1">{artist.location}</p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
};

export default UserProfilePage;
